import React, { useEffect, useState, useCallback } from 'react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { systemApi } from '../../services/api';
import { useWebSocket } from '../../hooks/useWebSocket';
import './Analytics.css';

const C = { accent:'#00e5ff', green:'#00ff88', warn:'#ffb800', danger:'#ff3d57', purple:'#b06cff', dim:'#1a2530' };
const Tip = ({ active, payload, label }: any) => !active || !payload?.length ? null : (
  <div className="an-tip"><div className="an-tip-time">{label}</div>
    {payload.map((p: any) => <div key={p.name} className="an-tip-row"><span style={{background:p.color,width:6,height:6,borderRadius:'50%',display:'inline-block',marginRight:6}}/><span style={{color:'#4a6070'}}>{p.name}</span><span style={{color:'#c8d8e8',marginLeft:8,fontWeight:600}}>{p.value}</span></div>)}
  </div>
);

export default function Analytics() {
  const [history, setHistory] = useState<any[]>([]);
  const [live, setLive] = useState<any>(null);
  useEffect(() => { systemApi.history().then(({ data }) => { const m = (data.results||data).reverse(); setHistory(m); if (m.length) setLive(m[m.length-1]); }).catch(()=>{}); }, []);
  const handleWs = useCallback((msg: any) => { if (msg.type === 'system_update') { setLive(msg.payload); setHistory(p => [...p.slice(-99), msg.payload]); } }, []);
  useWebSocket(handleWs);
  const fmt = (ts: string) => new Date(ts).toLocaleTimeString('az-AZ',{hour:'2-digit',minute:'2-digit'});
  const data = history.map(m => ({ time: fmt(m.timestamp), 'CPU Temp':m.cpu_temp, 'CPU Yük':m.cpu_load, 'RAM':m.ram_percent, 'Disk':m.disk_percent, 'Batareya':m.battery_percent, 'Siqnal':m.signal_strength, 'Yükləmə':m.net_rx_kbps, 'Göndərmə':m.net_tx_kbps }));
  const cls = (v: number, w: number, b: number) => v >= b ? 'bad' : v >= w ? 'warn' : 'ok';
  const L = live || {};
  return (
    <div className="an-page">
      <div className="an-stats">
        {[['CPU TEMP',`${L.cpu_temp||'—'}°C`,cls(L.cpu_temp,55,70)],['CPU YÜK',`${L.cpu_load||'—'}%`,cls(L.cpu_load,65,85)],['RAM',`${L.ram_percent||'—'}%`,cls(L.ram_percent,70,90)],['DİSK',`${L.disk_percent||'—'}%`,cls(L.disk_percent,70,90)],['BATAREYA',`${L.battery_percent||'—'}%`,'ok'],['SİQNAL',`${L.signal_strength||'—'}%`,'ok']].map(([l,v,c])=>(
          <div key={l} className="an-stat"><div className="an-sl">{l}</div><div className={`an-sv ${c}`}>{v}</div></div>
        ))}
      </div>
      <div className="an-range-bar"><div className="an-live"><div className="an-ldot"/>CANLI</div></div>
      {[
        { title:'CPU TEMPERATUR', keys:[{k:'CPU Temp',color:C.accent}], yDomain:[20,90] },
        { title:'CPU YÜK & RAM', keys:[{k:'CPU Yük',color:C.accent},{k:'RAM',color:C.green}], yDomain:[0,100] },
      ].map(({ title, keys, yDomain }) => (
        <div key={title} className="an-card">
          <div className="an-card-title">{title}</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} margin={{top:8,right:8,left:-20,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.dim} />
              <XAxis dataKey="time" tick={{fill:'#4a6070',fontSize:10,fontFamily:'Share Tech Mono'}} />
              <YAxis domain={yDomain} tick={{fill:'#4a6070',fontSize:10,fontFamily:'Share Tech Mono'}} />
              <Tooltip content={<Tip/>} />
              {keys.length > 1 && <Legend wrapperStyle={{fontFamily:'Share Tech Mono',fontSize:10,color:'#4a6070'}} />}
              {keys.map(({k,color}) => <Line key={k} type="monotone" dataKey={k} stroke={color} strokeWidth={2} dot={false} activeDot={{r:4}} />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ))}
      <div className="an-grid2">
        {[['BATAREYA','Batareya',C.green],['4G SİQNAL','Siqnal',C.purple],['DİSK','Disk',C.warn]].map(([title,key,color])=>(
          <div key={title} className="an-card">
            <div className="an-card-title">{title}</div>
            <ResponsiveContainer width="100%" height={150}>
              <AreaChart data={data} margin={{top:8,right:8,left:-20,bottom:0}}>
                <defs><linearGradient id={`g${key}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.2}/><stop offset="95%" stopColor={color} stopOpacity={0}/></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke={C.dim} />
                <XAxis dataKey="time" tick={{fill:'#4a6070',fontSize:9,fontFamily:'Share Tech Mono'}} />
                <YAxis domain={[0,100]} tick={{fill:'#4a6070',fontSize:9,fontFamily:'Share Tech Mono'}} />
                <Tooltip content={<Tip/>} />
                <Area type="monotone" dataKey={key} stroke={color} strokeWidth={2} fill={`url(#g${key})`} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
      <div className="an-card">
        <div className="an-card-title">İNTERNET İSTİFADƏSİ (KB/s)</div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data} margin={{top:8,right:8,left:-20,bottom:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.dim} />
            <XAxis dataKey="time" tick={{fill:'#4a6070',fontSize:10,fontFamily:'Share Tech Mono'}} />
            <YAxis tick={{fill:'#4a6070',fontSize:10,fontFamily:'Share Tech Mono'}} />
            <Tooltip content={<Tip/>} />
            <Legend wrapperStyle={{fontFamily:'Share Tech Mono',fontSize:10,color:'#4a6070'}} />
            <Line type="monotone" dataKey="Yükləmə" stroke={C.green} strokeWidth={2} dot={false} activeDot={{r:4}} />
            <Line type="monotone" dataKey="Göndərmə" stroke={C.warn} strokeWidth={2} dot={false} activeDot={{r:4}} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="an-foot">{history.length} nöqtə · Son: {live ? fmt(live.timestamp) : '—'}</div>
    </div>
  );
}
